
import { db, auth } from '../services/firebase-config';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  arrayUnion, 
  arrayRemove, 
  onSnapshot 
} from 'firebase/firestore';

// Get current user's watchlist
export async function getWatchlist() {
  try {
    if (!auth.currentUser) {
      console.warn('No authenticated user for watchlist');
      return [];
    }

    const userDoc = doc(db, 'watchlists', auth.currentUser.uid);
    const docSnap = await getDoc(userDoc);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.coins || [];
    } else {
      // Create empty watchlist document
      await setDoc(userDoc, { coins: [] });
      return [];
    }
  } catch (error) {
    console.error('Error fetching watchlist:', error);
    return [];
  }
}

// Add a coin to watchlist
export async function addToWatchlist(coinId) {
  try {
    if (!auth.currentUser) {
      throw new Error('No authenticated user');
    }

    if (!coinId) {
      throw new Error('Coin ID is required');
    }

    const userDoc = doc(db, 'watchlists', auth.currentUser.uid);
    
    // Check if document exists
    const docSnap = await getDoc(userDoc);
    
    if (docSnap.exists()) {
      // Update existing document
      await updateDoc(userDoc, {
        coins: arrayUnion(coinId)
      });
    } else {
      // Create new document
      await setDoc(userDoc, {
        coins: [coinId]
      });
    }

    console.log(`Added ${coinId} to watchlist`);
    return true;
  } catch (error) {
    console.error('Error adding to watchlist:', error);
    throw error;
  }
}

// Remove a coin from watchlist
export async function removeFromWatchlist(coinId) {
  try {
    if (!auth.currentUser) {
      throw new Error('No authenticated user');
    }

    if (!coinId) {
      throw new Error('Coin ID is required');
    }

    const userDoc = doc(db, 'watchlists', auth.currentUser.uid);
    
    await updateDoc(userDoc, {
      coins: arrayRemove(coinId)
    });

    console.log(`Removed ${coinId} from watchlist`);
    return true;
  } catch (error) {
    console.error('Error removing from watchlist:', error);
    throw error;
  }
}

// Check if a coin is in the watchlist
export async function isInWatchlist(coinId) {
  try {
    const watchlist = await getWatchlist();
    return watchlist.includes(coinId);
  } catch (error) {
    console.error('Error checking watchlist:', error);
    return false;
  }
}

// Toggle a coin in/out of watchlist
export async function toggleWatchlist(coinId) {
  try {
    const isWatched = await isInWatchlist(coinId);
    
    if (isWatched) {
      await removeFromWatchlist(coinId);
      return false; // Now removed
    } else {
      await addToWatchlist(coinId);
      return true; // Now added
    }
  } catch (error) {
    console.error('Error toggling watchlist:', error);
    throw error;
  }
}

// Subscribe to real-time watchlist updates
export function subscribeToWatchlist(callback) {
  if (!auth.currentUser) {
    console.warn('No authenticated user for watchlist subscription');
    return () => {}; // Return empty unsubscribe function
  }

  try {
    const userDoc = doc(db, 'watchlists', auth.currentUser.uid);
    
    const unsubscribe = onSnapshot(userDoc, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        callback(data.coins || []);
      } else {
        callback([]);
      }
    }, (error) => {
      console.error('Error in watchlist subscription:', error);
      callback([]); // Return empty array on error
    });

    return unsubscribe;
  } catch (error) {
    console.error('Error setting up watchlist subscription:', error);
    return () => {}; // Return empty unsubscribe function
  }
}

// Get watchlist with additional coin data
export async function getWatchlistWithData(coinsData) {
  try {
    const watchlist = await getWatchlist();
    
    if (!watchlist.length || !coinsData.length) {
      return [];
    }

    // Filter coins data to only include watchlisted coins
    const watchlistData = coinsData.filter(coin => watchlist.includes(coin.id));
    
    // Sort by watchlist order
    return watchlistData.sort((a, b) => {
      const aIndex = watchlist.indexOf(a.id);
      const bIndex = watchlist.indexOf(b.id);
      return aIndex - bIndex;
    });
  } catch (error) {
    console.error('Error getting watchlist with data:', error);
    return [];
  }
}

// Clear entire watchlist
export async function clearWatchlist() {
  try {
    if (!auth.currentUser) {
      throw new Error('No authenticated user');
    }

    const userDoc = doc(db, 'watchlists', auth.currentUser.uid);
    
    await updateDoc(userDoc, {
      coins: []
    });

    console.log('Watchlist cleared');
    return true;
  } catch (error) {
    console.error('Error clearing watchlist:', error);
    throw error;
  }
}

// Get watchlist statistics
export async function getWatchlistStats(coinsData) {
  try {
    const watchlistData = await getWatchlistWithData(coinsData);
    
    if (!watchlistData.length) {
      return {
        totalCoins: 0,
        totalValue: 0,
        totalChange: 0,
        averageChange: 0,
        bullishCount: 0,
        bearishCount: 0
      };
    }

    let totalValue = 0;
    let totalChange = 0;
    let validChangeCount = 0;
    let bullishCount = 0;
    let bearishCount = 0;

    watchlistData.forEach(coin => {
      if (coin.market_cap) {
        totalValue += coin.market_cap;
      }

      if (coin.price_change_percentage_24h !== null) {
        totalChange += coin.price_change_percentage_24h;
        validChangeCount++;

        if (coin.price_change_percentage_24h > 0) {
          bullishCount++;
        } else {
          bearishCount++;
        }
      }
    });

    const averageChange = validChangeCount > 0 ? totalChange / validChangeCount : 0;

    return {
      totalCoins: watchlistData.length,
      totalValue,
      totalChange,
      averageChange,
      bullishCount,
      bearishCount,
      neutralCount: watchlistData.length - bullishCount - bearishCount
    };
  } catch (error) {
    console.error('Error getting watchlist stats:', error);
    return {
      totalCoins: 0,
      totalValue: 0,
      totalChange: 0,
      averageChange: 0,
      bullishCount: 0,
      bearishCount: 0
    };
  }
}